import { useState } from 'react';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { HostedModel, ModelConnection } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { Empty, Field, Input, Mono, Page, PageHeader, Panel, Select, Skeleton, Table, col } from '@bounded/ui/primitives';
import { ErrorNotice } from '@bounded/ui/state';
import { Setting } from '../../components/setting';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/models')({ component: Models });

const TEMPLATE: ModelConnection = { id: 'hosted_new', provider: 'openrouter-chat', endpoint: 'https://openrouter.ai/api/v1/chat/completions', models: ['openai/gpt-5-mini'], allowed_context: ['/action/operations'], timeout_ms: 8000, max_input_bytes: 16384, max_output_tokens: 512 };

function Models() {
  const t = useT(); const m = t.models; const f = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const models = useQuery(q.models());
  const usage = useQuery(q.usage());
  const refresh = () => Promise.all(['models', 'audit'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
  const cost = (n: number | null) => n == null ? '–' : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(n);
  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      <p className="max-w-[72ch] rounded-[var(--radius-control)] border border-rule bg-surface px-3 py-2.5">{m.keys}</p>
      {me.permissions.mutate && <ConnectionForm onDone={refresh} />}
      {models.error ? <ErrorNotice error={models.error} onRetry={() => void models.refetch()} /> : (
        <Panel>
          {!models.data ? <div className="p-4"><Skeleton className="h-32" /></div> : !models.data.items.length ? <Empty title={m.empty} /> : (
            <ul>{models.data.items.map((c) => (
              <li key={c.id} className="grid gap-3 border-b border-rule px-4 py-3 last:border-b-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="grid min-w-0 gap-0.5">
                    <p className="font-mono font-medium">{c.id}</p>
                    <p className="text-graphite [overflow-wrap:anywhere]">{c.config.models.join(', ')}</p>
                    <p className="flex flex-wrap gap-x-4 text-sm text-graphite">
                      <span>{m.provider} <code>{c.config.provider}</code></span>
                      <span>{m.limit} <span className="tabular-nums text-ink">{f.number(c.daily_call_limit)}</span></span>
                      <span>{m.accountLimit} <span className="tabular-nums text-ink">{c.account_daily_limit == null ? m.noAccountLimit : f.number(c.account_daily_limit)}</span></span>
                      <span>{t.common.revision(c.revision)}</span>
                    </p>
                  </div>
                  <Setting on={!!c.enabled}>{c.enabled ? m.enabled : m.off}</Setting>
                </div>
                {me.permissions.mutate && <ConnectionForm existing={c} onDone={refresh} />}
              </li>
            ))}</ul>
          )}
        </Panel>
      )}
      <Panel title={m.usage} description={m.usageDescription}>
        {usage.error ? <div className="p-4"><ErrorNotice error={usage.error} onRetry={() => void usage.refetch()} /></div>
          : !usage.data ? <div className="p-4"><Skeleton className="h-24" /></div>
          : !usage.data.items.length ? <Empty title={m.usageEmpty} />
          : <Table minWidth="44rem" head={[{ label: m.day }, { label: m.connection }, { label: m.calls, className: col.num }, { label: m.unknown, className: col.num }, { label: m.tokensIn, className: col.num }, { label: m.tokensOut, className: col.num }, { label: m.cost, className: col.num }]}>
              {usage.data.items.map((u) => (
                <tr key={`${u.connection_id}/${u.day}`}>
                  <td className="tabular-nums">{u.day}</td><td><Mono value={u.connection_id} /></td>
                  <td className={col.num}>{f.number(u.calls)}</td><td className={col.num}>{f.number(u.unknown_calls)}</td>
                  <td className={col.num}>{u.input_tokens == null ? '–' : f.number(u.input_tokens)}</td><td className={col.num}>{u.output_tokens == null ? '–' : f.number(u.output_tokens)}</td>
                  <td className={col.num}>{cost(u.reported_cost)}</td>
                </tr>
              ))}
            </Table>}
      </Panel>
    </Page>
  );
}

/** Everything an admin sets for one hosted connection. The visual form and the JSON view both edit this object. */
type HostedConfig = { connection: ModelConnection; enabled: boolean; daily_call_limit: number; account_daily_limit: number | null };
const ENDPOINTS = { 'openrouter-chat': 'https://openrouter.ai/api/v1/chat/completions', 'openrouter-systemone': 'https://openrouter.ai/api/v1/systemone' } as const;
type HostedProvider = keyof typeof ENDPOINTS;
const CONTEXT = ['/action/operations', '/action/raw_tool', '/evidence'];
const ID = /^[a-z][a-z0-9_-]{0,95}$/;
const int = (v: unknown, min: number, max: number) => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;
/** Structural check for the JSON view; the server validates the connection itself. */
const shape = (v: unknown, notObject: string) => isObject(v) && isObject(v.connection) && typeof v.enabled === 'boolean' && typeof v.daily_call_limit === 'number'
  && (v.account_daily_limit === null || typeof v.account_daily_limit === 'number') ? null : notObject;

/** Create (no `existing`, expected revision 0) or replace a hosted connection. */
function ConnectionForm({ existing, onDone }: { existing?: HostedModel; onDone: () => Promise<unknown> }) {
  const t = useT(); const m = t.models; const u = m.fields;
  const initial = (): HostedConfig => ({ connection: structuredClone(existing?.config ?? TEMPLATE), enabled: existing ? !!existing.enabled : false,
    daily_call_limit: existing?.daily_call_limit ?? 200, account_daily_limit: existing?.account_daily_limit ?? null });
  const [value, setValue] = useState(initial);
  const [jsonOk, setJsonOk] = useState(true);
  const c = value.connection;
  const set = (patch: Partial<HostedConfig>) => setValue((v) => ({ ...v, ...patch }));
  const setConnection = (patch: Partial<ModelConnection>) => setValue((v) => ({ ...v, connection: { ...v.connection, ...patch } }));
  const models = c.models.filter((x) => x.trim());
  const valid = jsonOk && ID.test(c.id) && (!existing || c.id === existing.id) && c.provider in ENDPOINTS && models.length > 0 && c.allowed_context.length > 0
    && int(value.daily_call_limit, 1, 10_000) && (value.account_daily_limit === null || int(value.account_daily_limit, 1, 100_000))
    && int(c.timeout_ms, 100, 30_000) && int(c.max_input_bytes, 256, 65_536) && int(c.max_output_tokens, 64, 8_192);
  const body = { expected_revision: existing?.revision ?? 0, ...value, connection: { ...c, models } };
  const key = existing?.id ?? 'new';
  const number = (v: string) => (v.trim() === '' ? NaN : Number(v));
  return (
    <ReasonAction label={existing ? m.edit : m.add} title={existing ? m.editTitle(existing.id) : m.addTitle} consequence={m.consequence} valid={valid} payload={body}
      run={(reason, idempotency) => api.saveModel(c.id, { ...body, reason }, idempotency)}
      onDone={async () => { await onDone(); if (!existing) setValue(initial()); }}>
      <ConfigEditor id={`model-json-${key}`} value={value} onChange={setValue} onValidityChange={setJsonOk} validate={(v) => shape(v, t.configEditor.notObject)} rows={16}>
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={u.id} htmlFor={`m-id-${key}`} hint={existing ? u.idFixed : u.idHint}>
              <Input id={`m-id-${key}`} className="font-mono" required disabled={!!existing} value={c.id} onChange={(e) => setConnection({ id: e.target.value.trim() })} />
            </Field>
            <Field label={m.provider} htmlFor={`m-provider-${key}`} hint={<code className="[overflow-wrap:anywhere]">{c.endpoint}</code>}>
              <Select id={`m-provider-${key}`} value={c.provider} onChange={(e) => { const p = e.target.value as HostedProvider; setConnection({ provider: p, endpoint: ENDPOINTS[p] }); }}>
                {(Object.keys(ENDPOINTS) as HostedProvider[]).map((p) => <option key={p} value={p}>{u.providers[p]}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={m.models} htmlFor={`m-models-${key}`} hint={u.modelsHint}>
            <textarea id={`m-models-${key}`} rows={3} spellCheck={false} value={c.models.join('\n')} onChange={(e) => setConnection({ models: e.target.value.split('\n') })}
              className="w-full rounded-[var(--radius-control)] border border-rule-strong bg-surface px-2.5 py-1.5 font-mono text-sm" />
          </Field>
          <fieldset className="grid gap-1.5">
            <legend className="mb-1 text-sm font-medium">{u.context}</legend>
            {CONTEXT.map((pointer) => (
              <label key={pointer} className="flex items-center gap-2">
                <input type="checkbox" className="size-4 accent-[var(--boundary)]" checked={c.allowed_context.includes(pointer)}
                  onChange={(e) => setConnection({ allowed_context: e.target.checked ? [...c.allowed_context, pointer] : c.allowed_context.filter((x) => x !== pointer) })} />
                <code className="text-sm">{pointer}</code><span className="text-sm text-graphite">{u.contextHelp[pointer as keyof typeof u.contextHelp]}</span>
              </label>
            ))}
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={u.timeout} htmlFor={`m-timeout-${key}`} hint="100–30,000 ms"><Input id={`m-timeout-${key}`} type="number" value={Number.isNaN(c.timeout_ms) ? '' : c.timeout_ms} onChange={(e) => setConnection({ timeout_ms: number(e.target.value) })} /></Field>
            <Field label={u.inputBytes} htmlFor={`m-in-${key}`} hint="256–65,536"><Input id={`m-in-${key}`} type="number" value={Number.isNaN(c.max_input_bytes) ? '' : c.max_input_bytes} onChange={(e) => setConnection({ max_input_bytes: number(e.target.value) })} /></Field>
            <Field label={u.outputTokens} htmlFor={`m-out-${key}`} hint="64–8,192"><Input id={`m-out-${key}`} type="number" value={Number.isNaN(c.max_output_tokens) ? '' : c.max_output_tokens} onChange={(e) => setConnection({ max_output_tokens: number(e.target.value) })} /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={u.limit} htmlFor={`limit-${key}`} hint={u.limitHint}>
              <Input id={`limit-${key}`} type="number" min={1} max={10000} required value={Number.isNaN(value.daily_call_limit) ? '' : value.daily_call_limit} onChange={(e) => set({ daily_call_limit: number(e.target.value) })} />
            </Field>
            <Field label={u.accountLimit} htmlFor={`account-limit-${key}`} hint={u.accountLimitHint}>
              <Input id={`account-limit-${key}`} type="number" min={1} max={100000} placeholder={m.noAccountLimit} value={value.account_daily_limit ?? ''}
                onChange={(e) => set({ account_daily_limit: e.target.value.trim() === '' ? null : Number(e.target.value) })} />
            </Field>
            <label className="flex items-center gap-2 self-center pt-5"><input type="checkbox" checked={value.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="size-4 accent-[var(--boundary)]" />{u.enabled}</label>
          </div>
        </div>
      </ConfigEditor>
    </ReasonAction>
  );
}
